import React from 'react';

export interface ReflectorNetworkInterface {
  name: string;
  ip: string;
  mac: string;
}

export interface Reflector {
  name: string;
  id: string;
  status: 'Active' | 'Passive';
  version: string;
  lastSync: string;
  cloud: string;
  region: string;
  role: string;
  networkInterfaces: ReflectorNetworkInterface[];
  license: {
    type: string;
    serial: string;
  };
}



export const statusTagVariant = {
  Active: 'success',
  Passive: 'neutral',
};

export const roleTagVariant: { [key: string]: 'success' | 'neutral' | 'default' } = {
  'Active (Primary)': 'success',
  'Passive (Backup)': 'default',
};

import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericButton from '@ui-components/Button/GenericButton';
import { GenericTableColumn } from '@ui-components/Table/GenericTable';

export const reflectorColumns: GenericTableColumn<Reflector>[] = [
  {
    header: 'Name',
    accessor: 'name' as keyof Reflector,
    renderCell: (row: Reflector) => (
      <span className="text-semantic-text-brand underline cursor-pointer">{row.name}</span>
    ),
  },
  {
    header: 'ID',
    accessor: 'id' as keyof Reflector,
  },
  {
    header: 'Status',
    accessor: 'status' as keyof Reflector,
    renderCell: (row: Reflector) => (
      <GenericTag label={row.status} variant={statusTagVariant[row.status] as 'success' | 'neutral' | 'default'} size="small" rounded="full" />
    ),
  },
  {
    header: 'Version',
    accessor: 'version' as keyof Reflector,
  },
  {
    header: 'Last Sync',
    accessor: 'lastSync' as keyof Reflector,
  },
  {
    header: 'Cloud',
    accessor: 'cloud' as keyof Reflector,
    renderCell: (row: Reflector) => (
      <GenericTag label={row.cloud} variant={row.cloud === 'AWS' ? 'warning' : 'neutral'} size="small" rounded="full" />
    ),
  },
  {
    header: 'Region',
    accessor: 'region' as keyof Reflector,
    renderCell: (row: Reflector) => (
      <GenericTag label={row.region} variant="neutral" size="small" rounded="full" />
    ),
  },
  {
    header: 'Role',
    accessor: 'role' as keyof Reflector,
    renderCell: (row: Reflector) => (
      <GenericTag label={row.role} variant={roleTagVariant[row.role] as 'success' | 'neutral' | 'default'} size="small" rounded="full" />
    ),
  },
  {
    header: 'Network Interfaces',
    renderCell: (row: Reflector) => (
      <table className="min-w-full text-xs border border-gray-200 rounded">
        <thead>
          <tr className="bg-gray-50">
            <th className="px-2 py-1 font-medium text-left">Name</th>
            <th className="px-2 py-1 font-medium text-left">IP</th>
            <th className="px-2 py-1 font-medium text-left">MAC</th>
          </tr>
        </thead>
        <tbody>
          {row.networkInterfaces.map((iface) => (
            <tr key={iface.name} className="border-t border-gray-100">
              <td className="px-2 py-1">{iface.name}</td>
              <td className="px-2 py-1">{iface.ip}</td>
              <td className="px-2 py-1">{iface.mac}</td>
            </tr>
          ))}
        </tbody>
      </table>
    ),
  },
  {
    header: 'Actions',
    renderCell: (row: Reflector) => (
      <div className="flex gap-2">
        {row.status === 'Passive' && (
          <GenericButton label="Make Active" size="sm" variant="secondary" shape="ghost" />
        )}
        <GenericButton label="View Details" size="sm" variant="secondary" shape="ghost" />
        <GenericButton label="Remove from Cluster" size="sm" variant="danger" shape="filled" />
      </div>
    ),
  },
];

export const networkInterfacesColumns: GenericTableColumn<ReflectorNetworkInterface>[] = [
  { header: 'Name', accessor: 'name' as keyof ReflectorNetworkInterface },
  { header: 'IP', accessor: 'ip' as keyof ReflectorNetworkInterface },
  { header: 'MAC', accessor: 'mac' as keyof ReflectorNetworkInterface },
];
